import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface TimePickerProps {
  value: string;           // HH:MM (24-hour)
  onChange: (value: string) => void;
  min?: string;            // HH:MM — times before this are disabled
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Radix Popover `modal`. Defaults to false — the long-standing behaviour for
   * every existing consumer. Pass true when the picker lives inside a modal
   * Dialog: a non-modal popover does not pause the Dialog's focus trap, so on
   * touch devices the popover can be dismissed by the same tap that opened it.
   */
  modal?: boolean;
}

function parseHHMM(t: string): [number, number] | null {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(':').map(Number);
  return [h ?? 0, m ?? 0];
}

function fmt12(h: number, m: number): string {
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function to24(h12: number, ampm: 'AM' | 'PM'): number {
  if (ampm === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

export function TimePicker({
  value,
  onChange,
  min,
  placeholder = 'Pick a time',
  disabled = false,
  className,
  modal = false,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const [pendingAmPm, setPendingAmPm] = useState<'AM' | 'PM'>('AM');
  const hourRef  = useRef<HTMLDivElement>(null);
  const minRef   = useRef<HTMLDivElement>(null);
  const amPmRef  = useRef<HTMLDivElement>(null);

  const parsed = parseHHMM(value);
  const selH   = parsed?.[0] ?? null;   // 0–23 or null
  const selM   = parsed?.[1] ?? null;

  // 12-hour display values derived from 24-hour selH
  const selH12  = selH === null ? null : selH % 12 === 0 ? 12 : selH % 12;
  // if a value is set, derive AM/PM from it; otherwise use pendingAmPm so column stays active
  const selAmPm: 'AM' | 'PM' = selH !== null ? (selH >= 12 ? 'PM' : 'AM') : pendingAmPm;

  const minParsed = parseHHMM(min ?? '');
  const minH = minParsed?.[0] ?? null;
  const minM = minParsed?.[1] ?? null;

  function isHourDisabled(h24: number): boolean {
    if (minH === null) return false;
    return h24 < minH;
  }

  function isMinuteDisabled(m: number): boolean {
    if (minH === null || selH === null) return false;
    if (selH > minH) return false;
    if (selH < minH) return true;
    return minM !== null && m < minM;
  }

  // Scroll selected items into view when popover opens
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (hourRef.current && selH12 !== null) {
        const el = hourRef.current.querySelector<HTMLElement>(`[data-h="${selH12}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      if (minRef.current && selM !== null) {
        const el = minRef.current.querySelector<HTMLElement>(`[data-m="${selM}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      if (amPmRef.current && selAmPm !== null) {
        const el = amPmRef.current.querySelector<HTMLElement>(`[data-ap="${selAmPm}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  function handleHourClick(h12: number) {
    const ampm = selAmPm ?? 'AM';
    const h24  = to24(h12, ampm);
    if (isHourDisabled(h24)) return;
    let m = selM ?? 0;
    if (minH !== null && h24 === minH && minM !== null && m < minM) {
      m = minM;
    }
    onChange(`${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }

  function handleAmPmClick(ampm: 'AM' | 'PM') {
    setPendingAmPm(ampm);
    if (selH12 === null) return;
    const h24 = to24(selH12, ampm);
    const m   = selM ?? 0;
    onChange(`${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }

  function handleMinuteClick(m: number) {
    if (isMinuteDisabled(m)) return;
    const h = selH ?? 0;
    onChange(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-start gap-2 text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">
            {parsed ? fmt12(parsed[0], parsed[1]) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0 overflow-hidden" align="start">
        <div className="flex">
          {/* Hour column — 1–12 */}
          <div
            ref={hourRef}
            className="h-52 w-16 overflow-y-auto py-1 border-r border-border/60"
            style={{ scrollbarWidth: 'none' }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] font-semibold text-muted-foreground text-center py-1 sticky top-0 bg-popover">HR</p>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h12) => {
              const h24     = to24(h12, selAmPm ?? 'AM');
              const past    = isHourDisabled(h24);
              const selected = h12 === selH12;
              return (
                <button
                  key={h12}
                  data-h={h12}
                  type="button"
                  onClick={() => handleHourClick(h12)}
                  disabled={past}
                  className={cn(
                    'w-full py-1.5 text-sm text-center transition-colors',
                    selected && 'bg-primary text-primary-foreground font-semibold rounded-none',
                    !selected && !past && 'hover:bg-accent hover:text-accent-foreground cursor-pointer',
                    past && 'text-muted-foreground/30 cursor-not-allowed',
                  )}
                >
                  {h12}
                </button>
              );
            })}
          </div>

          {/* Minute column */}
          <div
            ref={minRef}
            className="h-52 w-16 overflow-y-auto py-1 border-r border-border/60"
            style={{ scrollbarWidth: 'none' }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] font-semibold text-muted-foreground text-center py-1 sticky top-0 bg-popover">MIN</p>
            {Array.from({ length: 60 }, (_, m) => m).map((m) => {
              const past     = isMinuteDisabled(m);
              const selected = m === selM;
              return (
                <button
                  key={m}
                  data-m={m}
                  type="button"
                  onClick={() => handleMinuteClick(m)}
                  disabled={past || selH === null}
                  className={cn(
                    'w-full py-1.5 text-sm text-center transition-colors',
                    selected && 'bg-primary text-primary-foreground font-semibold rounded-none',
                    !selected && !past && selH !== null && 'hover:bg-accent hover:text-accent-foreground cursor-pointer',
                    (past || selH === null) && 'text-muted-foreground/30 cursor-not-allowed',
                  )}
                >
                  {String(m).padStart(2, '0')}
                </button>
              );
            })}
          </div>

          {/* AM / PM column */}
          <div
            ref={amPmRef}
            className="h-52 w-14 overflow-y-auto py-1"
            style={{ scrollbarWidth: 'none' }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] font-semibold text-muted-foreground text-center py-1 sticky top-0 bg-popover">AM/PM</p>
            {(['AM', 'PM'] as const).map((ap) => (
              <button
                key={ap}
                data-ap={ap}
                type="button"
                onClick={() => handleAmPmClick(ap)}
                className={cn(
                  'w-full py-1.5 text-sm text-center transition-colors cursor-pointer',
                  selAmPm === ap && 'bg-primary text-primary-foreground font-semibold rounded-none',
                  selAmPm !== ap && 'hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {ap}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
